import moment from "moment"

import { ITableColumn } from "components/UI/organisms/Table/TableTypes"
import { IReservation } from "types/reservation"
import { Box, Flex, Text, Tooltip, Image } from "@chakra-ui/react"
import SelectInput from "components/UI/atoms/Select/Select"
import CarNumberBox from "components/UI/molecules/CarNumberBox"
import Popover from "components/UI/atoms/Popover"
import { IRoles, LANGS } from "types/shared"



interface IColumnsArgs {
  handleBoxSelectChange: (id: number, val: number) => void
  handleWasherSelectChange: (id: number, val: number) => void
  updateReservationBoxLoading: boolean
  updateReservationWasherLoading: boolean
  lang: LANGS
  reservedText: any
  isDisabled: boolean
  boxData: any
  priceColor: string
  noBoxTranslated: string
  userRole: any
  noWasherTranslated: string
}


const renderLaundry = (reservation: IReservation) => (
  <Box
    bgColor="white"
    borderRadius="base"
    boxShadow="0px 5px 20px #046CEC40"
    padding="15px"
    transform="translateX(-20px)"
    pos="relative"
  >
    <Box
      pos="absolute"
      w="0"
      h="0"
      left="12px"
      bottom="-8px"
      borderLeft="8px solid transparent"
      borderRight="8px solid transparent"
      borderTop="8px solid white"
    ></Box>
    <Flex alignItems="center">
      <Image w="45px" src={reservation?.carWashLogo} />
      <Text ml="20px" fontSize="base" color="main_text_color">{reservation.carWash}, {reservation.carWashAddress}</Text>
    </Flex>
  </Box>
)

const renderCar = (model: string, bodyType: string) => (
  model != null && model.length > 18 ? (
    <Popover
      rootElement={
        <Text as="span">{model.substring(0, 18)}... <Box as="span" fontWeight="bold" ml="3px">{`(${bodyType})`}</Box></Text>
      }
      content={model}
      placement="top-start"
      trigger="hover"
    />
  ) : (
    <>
      {model}
      <Box as="span" fontWeight="bold" ml="3px">{`(${bodyType})`}</Box>
    </>
  )
)

const generateBoxOptions = (quantity: number, noBoxTranslated: string) => (
  [
    {
      label: noBoxTranslated,
      value: 0
    },
    ...Array.from({ length: quantity }, (_v, i: number) => ({
      label: `#${i + 1}`,
      value: i + 1
    }))
  ]
);






export const getColumns = (t: any, {
  handleBoxSelectChange,
  handleWasherSelectChange,
  updateReservationBoxLoading,
  updateReservationWasherLoading,
  reservedText,
  isDisabled,
  boxData,
  priceColor,
  noBoxTranslated,
  noWasherTranslated,
  userRole
}: IColumnsArgs): ITableColumn[] => [
    {
      key: "reserved",
      render: (data: IReservation) => (
        !data?.webReservation ?
          <Box style={{
            backgroundColor: "#08BA36",
            width: "20px",
            left: "0",
            top: "0",
            bottom: "1px",
            position: "absolute",
            borderRadius: "5px 0 0 5px",
            writingMode: "vertical-rl",
          }}>
            <Text style={{ fontSize: "9px", color: "#FFF", transform: "rotate(180deg)", textAlign: "center", marginRight: "-2.7px" }}>{reservedText}</Text>
          </Box> : <></>
      )
    },
    {
      key: "bookings.table.columns.id",
      label: t("bookings.table.columns.id"),
      render: (data: IReservation) => (
        <Tooltip label={renderLaundry(data)} placement="top-start" bg="transparent" boxShadow="none">
          <Text fontWeight="bold">{data?.number}</Text>
        </Tooltip>
      ),
    },
    {
      key: "bookings.table.columns.auto",
      label: t("bookings.table.columns.auto"),
      render: (data: IReservation) => (
        <Box>
          {renderCar(data?.vehicleModel, data?.vehicleBodyType)}
          <CarNumberBox height="35px" width="120px" title={data?.vehicleGovNumber.toUpperCase()} />
        </Box>
      ),
    },
    {
      key: "bookings.table.columns.booking_date",
      label: t("bookings.table.columns.booking_date"),
      render: (data: IReservation) => (
        <Box>
          <Text>{moment(data?.startDateTime).format("DD MMMM YYYY")}</Text>
          <Flex>
            <Text fontWeight="bold">{moment(data?.startDateTime).format("HH:mm A")}</Text>
          </Flex>
        </Box>
      ),
    },
    {
      key: "bookings.table.columns.service",
      label: t("bookings.table.columns.service"),
      render: (data: IReservation) => {
        const isQappUser = userRole && [IRoles.QAPP_ADMIN, IRoles.QAPP_MANAGER].includes(userRole);
        let price
        if (data?.webReservation) {
          price = data?.toTalPrice
        } else if (isQappUser) {
          price = data?.toTalPrice
        } else {
          price = data?.price
        }

        return (
          <Flex alignItems="center" gap={"5px"}>
            <Box w="55px">
              <Text
                whiteSpace={"pre-line"}
                wordBreak={"break-all"}
                display="block"
                fontSize="11px"
                lineHeight="14px"
                fontWeight="bold"
              >
                {data?.washType.split(" ")[0] || ""}
              </Text>
              <Text
                whiteSpace={"pre-line"}
                wordBreak={"break-all"}
                display="block"
                fontSize="11px"
                lineHeight="14px"
                fontWeight="bold"
              >
                {data?.washType.split(" ")[1] || ""}
              </Text>
            </Box>
            <Flex
              w="40px"
              h="28px"
              color={priceColor}
              backgroundColor="success_1"
              alignItems="center"
              justifyContent="center"
              borderRadius="14px"
              fontWeight="bold"
            >
              {price}₾
            </Flex>
          </Flex>
        )
      }
    },
    {
      key: "bookings.table.columns.wash_box",
      label: t("bookings.table.columns.wash_box"),
      render: (data: IReservation) => {

        const boxDataWasherEmployees = boxData
          .filter((box: any) => box.carWashId === data.carWashId)
          .map((box: any) => box.washers.map((washer: any) => washer.washerEmployee));


        const boxDataWasherEmployeesIds = boxData
          .filter((box: any) => box.carWashId === data.carWashId)
          .map((box: any) => box.washers.map((washer: any) => washer.washerEmployeeId));



        const combinedData = [
          {
            label: boxDataWasherEmployees.flat(),
            value: boxDataWasherEmployeesIds.flat()
          }
        ]
        interface Option {
          label: string;
          value: number;
        }

        function createOptions(data: { label: string[], value: number[] }[]): Option[] {
          const options: Option[] = [{ label: noWasherTranslated, value: 0 }]; // Add initial option

          data.forEach((item, index) => {
            item.label.forEach((label, i) => {

              options.push({
                label: label.substring(0, 15),
                value: item.value[i]
              });
            });
          });

          return options;
        }

        return (
          <Flex style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            maxWidth: "205px",
            marginLeft: "-15px"
          }} >
            <Flex
              alignItems="center"
              justifyContent="center"
              w="100px"
              h="28px"
              borderRadius="12px"
              fontWeight="bold"
              flexGrow={1}
            >
              <SelectInput // This is how to style React-Select 
                styles={{
                  control: (baseStyles: any, state: { isFocused: any }) => ({
                    ...baseStyles,
                    backgroundColor: "#046CEC",
                    isFocused: "red"

                  }),
                  singleValue: (baseStyles: any) => ({
                    ...baseStyles,
                    color: "#FFFFFF",
                  }),
                  option: (baseStyles: any, { isDisabled, isFocused, isSelected }: { isDisabled: boolean, isFocused: boolean, isSelected: boolean }) => ({
                    ...baseStyles,
                    color: isSelected ? "#FFFFFF" : "#000000",
                    backgroundColor: isSelected ? "#046CEC" : isFocused ? "#d6ddf9" : "#FFF",
                    fontSize: "12px",
                    fontWeight: "bold"

                  }),
                  placeholder: (baseStyles: any) => ({
                    ...baseStyles,
                    color: "#d9dfe2",
                  }),
                  menu: (baseStyles: any) => ({
                    ...baseStyles,
                    width: "170px",
                  }),

                }}
                inputStyles={{ width: "120px", }}
                value={data?.washerEmployeeId || ""}
                options={createOptions(combinedData)}
                onChange={(e: any) => {
                  handleWasherSelectChange(data?.id, e.value)
                  // e.preventDefault()
                }}
                isLoading={updateReservationWasherLoading}
                isDisabled={isDisabled}
              />
            </Flex>
            <Flex >
              <SelectInput
                styles={{
                  menu: (baseStyles: any) => ({
                    ...baseStyles,
                    width: "80px",
                  }),
                  option: (baseStyles: any) => ({
                    ...baseStyles,
                    fontSize: "12px",

                  }),
                }}
                inputStyles={{ width: "60px" }}
                value={data?.boxNumber || ""}
                options={generateBoxOptions(data?.boxCount, noBoxTranslated)}
                onChange={(e: any) => {
                  handleBoxSelectChange(data?.id, e.value)
                  // e.preventDefault()
                }}
                isLoading={updateReservationBoxLoading}
                isDisabled={isDisabled}
              />
            </Flex>
          </Flex>
        )
      }
    },
    {
      key: "bookings.table.columns.status",
      label: t("bookings.table.columns.status"),
      render: (data: any) => (
        <Box
          w="85px"
          whiteSpace={"pre-line"}
          wordBreak={"break-all"}
        >
          <Text fontSize="base" fontWeight="bold">{data?.statusText}</Text>
        </Box>
      )
    },
  ]
